import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import ContentWrapper from '../Layout/ContentWrapper';
import { Card, CardHeader, CardFooter, CardBody, Input, Col, Button } from 'reactstrap';
import * as applicantService from '../Application/Services/applicantService';
import * as queryString from 'query-string';
import ApplicantsTable from './Components/Tables/applicantsTable';
import Pagination from './Components/shared/pagination';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import MostSuitableJobsModal from './Components/Modals/mostSuitableJobsModal';
import HelpModal from './Components/Modals/helpModal';
import * as utils from '../Application/Services/utils';
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import { SpinnerLoader } from './Components/shared/spinnerLoader';
import useConfigStore from '../../stores/configStore';

const pageSizeFromLocalStore = localStorage.getItem('numItemsPerPageApplicants') ?? '10';

const initialApplicantParams = {
  FirstName: '',
  LastName: '',
  JobId: '0',
  Type: 'All',
  PageNumber: 1,
  PageSize: pageSizeFromLocalStore,
  Status: 'All',
  OnlyNew: false,
  Competence: '',
  MineOrAllSelected: '1',
  City: '',
  DistanceFromCity: utils.getDistance()[0],
  Lat: 0,
  Lng: 0,
};

const Applicants = ({ location, history }) => {
  const { t } = useTranslation();
  const gApi = useConfigStore((state) => state.gApi);

  const [totalItems, setTotalItems] = useState(0);
  const [selectTypeValue, setSelectTypeValue] = useState(utils.getApplicantTypes()[0]);
  const [selectStatusValue, setSelectStatusValue] = useState(utils.getApplicantStatus()[0]);
  const [applicants, setApplicants] = useState([]);
  const [forSpecificJob, setForSpecificJob] = useState(false);
  const [applicantParams, setApplicantParams] = useState(initialApplicantParams);
  const [mostSuitableJobs, setMostSuitableJobs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCompetencesInTable, setShowCompetencesInTable] = useState(false);
  const [showCompetencesInTable2, setShowCompetencesInTable2] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(pageSizeFromLocalStore);
  const [pagination, setPagination] = useState({});

  // 1. First, modify the fetchApplicants callback:
  const fetchApplicants = useCallback(async (params) => {
    try {
      setLoading(true);
      const { data, headers } = await applicantService.getApplicants(params);
      const paginationData = JSON.parse(headers.pagination);

      setApplicants(data);
      setTotalItems(paginationData.totalItems);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching applicants:', error);
      // Add error state if needed
      setApplicants([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since it doesn't use any external values

  // 2. Then modify the refreshData callback:
  const refreshData = useCallback(async () => {
    const numItemsPerPageCompetences = localStorage.getItem('numItemsPerPageApplicants') ?? '10';
    const parsed = queryString.parse(location.search);

    const newParams = {
      ...initialApplicantParams, // Use initialApplicantParams instead of current state
      PageNumber: 1,
      OnlyNew: Boolean(parsed.onlyNew),
      FirstName: parsed.name || '',
      LastName: parsed.secondName || '',
      MineOrAllSelected: '1', // Set default value directly
    };

    setItemsPerPage(numItemsPerPageCompetences);
    setSelectTypeValue(utils.getApplicantTypes()[0]);
    setSelectStatusValue(utils.getApplicantStatus()[0]);
    setShowCompetencesInTable(false);
    setShowCompetencesInTable2(false);
    setApplicantParams(newParams);

    await fetchApplicants(newParams);
  }, [location.search, fetchApplicants]); // Remove applicantParams from dependencies

  // 3. Update the useEffect:
  // useEffect(() => {
  //   const init = async () => {
  //     await refreshData();
  //   };
  //   init();

  //   return () => {
  //     debouncedSearchByString.cancel();
  //   };
  // }, [refreshData]);

  const debouncedSearchByString = useCallback(
    debounce(async (updatedParams) => {
      try {
        const { data, headers } = await applicantService.getApplicants(updatedParams);
        const paginationData = JSON.parse(headers.pagination);
        setApplicants(data);
        setTotalItems(paginationData.totalItems);
        setShowCompetencesInTable2(showCompetencesInTable);
        setPagination(paginationData);
      } catch (error) {
        console.error('Error in search:', error);
      }
    }, 1000),
    [showCompetencesInTable]
  );

  useEffect(() => {
    refreshData();
    return () => {
      debouncedSearchByString.cancel();
    };
  }, [refreshData, debouncedSearchByString]);

  const handleCitySelect = async (address) => {
    const placeId = address.value.place_id;
    const mainName = address.value.structured_formatting.main_text;
    const cityTmp = { label: mainName, value: mainName };

    try {
      const results = await geocodeByPlaceId(placeId);
      const { lat, lng } = await getLatLng(results[0]);

      const newParams = {
        ...applicantParams,
        City: cityTmp,
        Lat: lat,
        Lng: lng,
        PageNumber: 1,
      };

      setApplicantParams(newParams);
      await fetchApplicants(newParams);
    } catch (error) {
      console.error('Error selecting city:', error);
    }
  };

  const onDistanceChanged = async (item) => {
    const newParams = {
      ...applicantParams,
      DistanceFromCity: item,
      PageNumber: 1,
    };

    setApplicantParams(newParams);
    await fetchApplicants(newParams);
  };

  const handleTableSizeChangeSelect = async (newVal) => {
    const newParams = {
      ...applicantParams,
      PageSize: newVal.value,
      PageNumber: 1,
    };

    setApplicantParams(newParams);
    setItemsPerPage(newVal.value);
    localStorage.setItem('numItemsPerPageApplicants', newVal.value);
    await fetchApplicants(newParams);
  };

  const handlePaginationSelected = async (selectedPage) => {
    const newParams = {
      ...applicantParams,
      PageNumber: selectedPage,
    };
    setApplicantParams(newParams);
    await fetchApplicants(newParams);
  };

  const onSelectTypeChanged = async (item) => {
    const newParams = {
      ...applicantParams,
      Type: item.value,
      PageNumber: 1,
    };
    setApplicantParams(newParams);
    setSelectTypeValue(item);
    await fetchApplicants(newParams);
  };

  const onSelectStatusChanged = async (item) => {
    const newParams = {
      ...applicantParams,
      Status: item.value,
      PageNumber: 1,
    };
    setApplicantParams(newParams);
    setSelectStatusValue(item);
    await fetchApplicants(newParams);
  };

  const searchByName = (text) => {
    const updatedParams = {
      ...applicantParams,
      FirstName: text,
      PageNumber: 1,
    };
    setApplicantParams(updatedParams);
    debouncedSearchByString(updatedParams);
  };

  const searchBySecondName = (text) => {
    const updatedParams = {
      ...applicantParams,
      LastName: text,
      PageNumber: 1,
    };
    setApplicantParams(updatedParams);
    debouncedSearchByString(updatedParams);
  };

  const searchByCompetence = (text) => {
    const updatedParams = {
      ...applicantParams,
      Competence: text,
      PageNumber: 1,
    };
    setApplicantParams(updatedParams);
    debouncedSearchByString(updatedParams);
  };

  const resetParams = async () => {
    await history.push('/applicants');
    setApplicantParams(initialApplicantParams);
    await refreshData();
  };

  const handleMostSuitableJobs = (jobs) => {
    setShowModal(true);
    setMostSuitableJobs(jobs);
  };

  return (
    <ContentWrapper>
      <MostSuitableJobsModal
        mostSuitableJobs={mostSuitableJobs}
        onToggle={() => setShowModal(!showModal)}
        showModal={showModal}
      />
      <div className="content-heading">
        <Col md={10}>
          <Trans i18nKey="applicants.APPLICANTS" />
          &nbsp;
          {/* <HelpModal
            purpose="applicantsHelp"
            showModal={showHelpModal}
            onToggle={() => setShowHelpModal(!showHelpModal)}
          /> */}
          <small>
            <Trans i18nKey="dashboard.WELCOME_BETA" />
          </small>
        </Col>
      </div>

      {loading ? (
        <SpinnerLoader />
      ) : (
        <Card className="card-default">
          <CardHeader>
            <div className="form-row align-items-center">
              <div style={{ width: '200px' }}>
                <GooglePlacesAutocomplete
                  apiKey={gApi}
                  selectProps={{
                    value: applicantParams.City,
                    onChange: handleCitySelect,
                    placeholder: t('applicants.SEARCH_BY_LOCATION'),
                  }}
                />
              </div>
              <div style={{ width: '100px' }}>
                <Select
                  name="applicantDistanceFromCitySelect"
                  value={applicantParams.DistanceFromCity}
                  onChange={onDistanceChanged}
                  options={utils.getDistance()}
                  placeholder="Km"
                  isDisabled={!applicantParams.City}
                />
              </div>
              <div className="col-auto">
                <Input
                  type="text"
                  value={applicantParams.FirstName}
                  onChange={(e) => searchByName(e.target.value)}
                  id="inlineFormInput"
                  placeholder={t('applicants.SEARCH_BY_NAME')}
                />
              </div>
              <div className="col-auto">
                <Input
                  type="text"
                  value={applicantParams.LastName}
                  onChange={(e) => searchBySecondName(e.target.value)}
                  id="inputSecondName"
                  placeholder={t('common.LAST_NAME')}
                />
              </div>
              <div className="col-auto">
                <Input
                  type="text"
                  value={applicantParams.Competence}
                  onChange={(e) => searchByCompetence(e.target.value)}
                  id="inputCompetence"
                  placeholder={t('competences.COMPETENCE')}
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  name="applicantStatusSelect"
                  value={selectStatusValue}
                  onChange={onSelectStatusChanged}
                  options={utils.getApplicantStatus()}
                />
              </div>
              &nbsp;
              <div style={{ width: '150px' }}>
                <Select
                  name="tags"
                  value={selectTypeValue}
                  onChange={onSelectTypeChanged}
                  options={utils.getApplicantTypes()}
                />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={resetParams}>
                  <Trans i18nKey="common.CLEAR_FILTERS" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <ApplicantsTable
              jobId={applicantParams.JobId}
              applicants={applicants}
              forSpecificJob={forSpecificJob}
              counting={(applicantParams.PageNumber - 1) * applicantParams.PageSize}
              onMostSuitableJobsReceived={handleMostSuitableJobs}
              onStatusRefresh={refreshData}
              showCompetencesInTable={showCompetencesInTable2}
            />
          </CardBody>
          <CardFooter>
            <Pagination
              totalItems={totalItems}
              pageSize={applicantParams.PageSize}
              activePage={applicantParams.PageNumber}
              onPaginationSelect={handlePaginationSelected}
              onTableSizeSelect={handleTableSizeChangeSelect}
              pagination={pagination}
            />
          </CardFooter>
        </Card>
      )}
    </ContentWrapper>
  );
};

export default Applicants;
